import { CaretRightIcon } from "@phosphor-icons/react/dist/csr/CaretRight";
import { SentChatImage } from "@/core/components/chat-images";
import { ChatMarkdown } from "@/core/components/chat-markdown";
import { Badge } from "@volca/ui/components/ui/badge";
import { Button } from "@volca/ui/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@volca/ui/components/ui/collapsible";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { cn } from "@volca/ui/lib/utils";
import { DocumentChip } from "@/profitability/document-chip";
import type { TemplateChatMessage } from "@/profitability/types";
import { templateChatPath } from "./template-chat-query";
import { CONTEXT_SUMMARY } from "./use-template-chat";

/**
 * Where the conversation was summarized: what is above stays on the page,
 * and Volca's replies after it read the notes instead.
 */
const SummaryDivider = ({
  message,
  first,
}: {
  message: TemplateChatMessage;
  first: boolean;
}) => (
  <Collapsible
    className={cn("w-full", { "mt-6": !first })}
    data-testid="chat-summary"
  >
    <Stack direction="row" align="center" gap={2} className="w-full">
      <div className="h-px flex-1 bg-line" aria-hidden />
      <CollapsibleTrigger
        render={<Button variant="ghost" size="sm" className="group" />}
      >
        <CaretRightIcon
          aria-hidden
          className="transition-transform group-data-[panel-open]:rotate-90"
        />
        Earlier messages summarized
      </CollapsibleTrigger>
      <div className="h-px flex-1 bg-line" aria-hidden />
    </Stack>
    <CollapsibleContent>
      <Stack gap={1} className="rounded-lg bg-surface-sunken px-3 py-2.5">
        <Text variant="caption" tone="muted">
          Volca reads these notes instead of the messages above.
        </Text>
        <ChatMarkdown>{message.content}</ChatMarkdown>
      </Stack>
    </CollapsibleContent>
  </Collapsible>
);

export const TemplateChatMessageView = ({
  businessId,
  reportId,
  message,
  first,
  onReview,
}: {
  businessId: string;
  reportId: string;
  message: TemplateChatMessage;
  first: boolean;
  /** Opens the version this build made, beside the conversation. */
  onReview?: () => void;
}) => {
  if (message.kind === CONTEXT_SUMMARY)
    return <SummaryDivider message={message} first={first} />;

  if (message.role === "user")
    return (
      <Stack
        gap={1}
        align="end"
        className={cn("w-full", cn({ "mt-6": !first }))}
        data-testid="chat-user-message"
      >
        {message.documents.length > 0 && (
          <Stack
            align="start"
            direction="row"
            gap={1}
            className="w-full max-w-[85%] flex-wrap justify-end"
          >
            {message.documents.map((document) => (
              <DocumentChip key={document.id} document={document} />
            ))}
          </Stack>
        )}
        {message.images.length > 0 && (
          <Stack
            align="end"
            direction="row"
            gap={2}
            className="w-full max-w-[85%] flex-wrap justify-end"
          >
            {message.images.map((image) => (
              <SentChatImage
                key={image.id}
                path={`${templateChatPath(businessId, reportId)}/images/${image.id}`}
                image={image}
              />
            ))}
          </Stack>
        )}
        <div className="max-w-[85%] rounded-lg border border-line px-3 py-2.5">
          <Text className="[overflow-wrap:anywhere] whitespace-pre-wrap">
            {message.content}
          </Text>
        </div>
      </Stack>
    );

  return (
    <Stack
      align="start"
      gap={1}
      className={cn("w-full", cn("min-w-0", { "mt-6": !first }))}
      data-testid="chat-assistant-message"
    >
      {message.kind === "build_update" && (
        <Badge
          variant="secondary"
          color="information"
          size="sm"
          className="w-fit"
        >
          Build
        </Badge>
      )}
      <ChatMarkdown>{message.content}</ChatMarkdown>
      {onReview && (
        <Button variant="secondary" size="sm" onClick={onReview}>
          Review this version
        </Button>
      )}
    </Stack>
  );
};
